import PageHeader from 'components/Layout/PageHeader'
import React, { ReactNode, useCallback, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { RootState, store } from 'store'
import Card from '../../../components/Layout/Card'
import LiveMediaMap from '../../Campaign/CampaignForm/LiveMediaMap'
import FormRow from '../../../components/Form/FormRow'
import FormColumn from '../../../components/Form/FormColumn'
import Input, { InputType } from '../../../components/Form/Input'
import { voohCampaignFormActions } from './slice'
import DatePickerSingle from '../../../components/Form/DatePickerSingle'
import { DATE_PLACEHOLDER } from '../../../constant'
import Select from '../../../components/Form/Select'
import InputTitle from '../../../components/Form/InputTitle'
import Button, { ButtonSize, ButtonTheme } from '../../../components/Form/Button'
import Link from '../../../components/Link'
import './VoohCampaignForm.scss'
import VoohMediaSelection from '../VoohMediaSelection/VoohMediaSelection'
import {
  VoohCreation,
  VoohMediaCheckboxTreeElement,
  VoohMediaFormat,
  VoohMediaNode,
  VoohUsedMedia,
} from './types'
import TsxUtils from '../../../utils/tsx'
import Modal from '../../../components/Modal'
import VoohMediaListTable from '../VoohMediaList/VoohMediaListTable'
import { CampaignFormHashes } from '../../Campaign/CampaignForm'
import {
  CampaignStatus,
  Medium,
  MediumPlacement,
  MediumStatus,
  OrganizationAttributes,
  VOOH_OFFER,
} from '../../../types/campaign'
import offerService from '../../Campaign/CampaignForm/DetailsForm/services/offer-form.service'
import { AppContext } from '../../../contexts/AppContext'
import { faArchive, faPlus, faTrashAlt } from '@fortawesome/free-solid-svg-icons'
import VoohCreationsFormUpload from '../VoohCreation/VoohCreationUploud/VoohCreationFormUploud'
import FlexRow from '../../../components/Layout/FlexRow'
import FlexCol from '../../../components/Layout/FlexCol'
import TooltipHelp from '../../../components/TooltipHelp'
import Preview from '../../../components/Preview'
import VoohCampaignFormService from './service'
import SelectAsync from '../../../components/Form/SelectAsync'
import debounce from 'lodash/debounce'
import AttributesModal from '../../Campaign/CampaignForm/DetailsForm/AttributesModal'
import { AgencyType } from '../../../types/agency'
import { CAMPAIGN_ACTION, CAMPAIGN_FORM } from '../../../constant/authorization'
import { BaseRole } from '../../../types/user'
import VoohCampaignActionButtons from './VoohCampaignActionButtons/VoohCampaignActionButtons'
import { FormType, ReactRouterParams } from '../../../types/various'
import { MediaDurationHelper } from '../../Campaign/CampaignForm/DetailsForm/services/media-duration.service'
import { Routes } from '../../../routes'
import FillingSpinner, { FillingSpinnerType } from '../../../components/FillingSpinner'
import Checkbox from '../../../components/Form/Checkbox'
import voohMediaSelectionService from '../VoohMediaSelection/service'
import { useNavigate, useParams } from 'react-router-dom'
import VoohCreateMediaTemplateModal from './VoohCreateMediaTemplateModal/voohCreateMediaTemplateModal'
import { FormCreation } from '../../../contexts/CampaignFormContext'
import ModalAction from '../../../components/Modal/ModalAction'
import CreationHelper from '../../../utils/creation-helper'
import NoteSummary from '../../Campaign/CampaignForm/NoteSummary'

const DEBOUNCE_POST_DELAY = 1000 // [ms]

const VoohCampaignForm: React.FC = () => {
  const { t } = useTranslation()
  const note = useSelector((state: RootState) => state.voohCampaignForm.note)
  const voohCardStatus = useSelector((state: RootState) => state.voohCampaignForm.cardStatus)
  const voohForm = useSelector((state: RootState) => state.voohCampaignForm.form)
  const touched = useSelector((state: RootState) => state.voohCampaignForm.touched)
  const [isMediaSelectionModalOpen, setIsMediaSelectionModalOpen] = useState<boolean>(false)
  const [isMediaTableOpen, setIsMediaTableOpen] = useState<boolean>(false)

  const mediaTree = useSelector((state: RootState) => state.voohCampaignForm.form.mediums)
  const voohFormErrors = useSelector((state: RootState) => state.voohCampaignForm.errors)
  const campaignStatus = useSelector((state: RootState) => state.voohCampaignForm.status)

  const [nodes, setNodes] = useState([])
  const [filteredNodes, setFilteredNodes] = useState<VoohMediaCheckboxTreeElement[]>([])
  const [filterNodesText, setFilterNodesText] = useState('')
  const [checkedNodes, setCheckedNodes] = useState<string[]>([])
  const [mapMedia, setMapMedia] = useState<Medium[]>([])
  const [isCreationsCardCollapsed, setIsCreationsCardCollapsed] = useState<boolean>(false)
  const { allowedFor, userData } = useContext(AppContext)
  const [icon, setIcon] = useState<string>()
  const { id: urlId } = useParams<ReactRouterParams>()
  const [isAddCreationModalOpen, setIsAddCreationModalOpen] = useState<boolean>(false)
  const [isAttributesModalOpen, setIsAttributesModalOpen] = useState<boolean>(false)
  //todo use setShouldSaveMedia to handle templates
  const [shouldSaveMedia, setShouldSaveMedia] = useState<boolean>(false) // eslint-disable-line
  const [saveTemplate, setSaveTemplate] = useState<boolean>(false)
  //todo use setTemplateMedia to handle templates
  const [templateMedia, setTemplateMedia] = useState<boolean>(false) // eslint-disable-line
  const navigate = useNavigate()
  const formType = urlId ? FormType.EDIT : FormType.NEW
  const [removeCreationModalIsOpenFor, setRemoveCreationModalIsOpenFor] = useState<
    VoohCreation['id'] | null
  >(null)
  const [archiveModalIsOpenFor, setArchiveModalIsOpenFor] = useState<FormCreation['id'] | null>(
    null
  )
  const [editArchivedModalOpenFor, setEditArchivedModalOpenFor] = useState<
    FormCreation['id'] | null
  >(null)
  const [archiveCreationLoading, setArchiveCreationLoading] = useState<boolean>(false)
  const [deleteCreationLoading, setDeleteCreationLoading] = useState<boolean>(false)

  let checkedMediaOnFilterById: string[] = []

  const debouncedCreationUpdate = useCallback(
    debounce(VoohCampaignFormService.updateCreation, DEBOUNCE_POST_DELAY),
    []
  )

  const debouncedBasicValuesSubmit = useCallback(
    debounce(VoohCampaignFormService.submitBasicValues, DEBOUNCE_POST_DELAY),
    []
  )

  const debouncedDetailsValuesSubmit = useCallback(
    debounce(VoohCampaignFormService.submitDetailsValues, DEBOUNCE_POST_DELAY),
    []
  )

  const disabledSupervisors = (): boolean => {
    if (userData.user?.roles.some(role => role.baseRole === BaseRole.SUPPORT)) {
      return false
    }
    return voohForm.baseValues.agency === null || !voohForm.id
  }

  const canEditNote = allowedFor({
    template: CAMPAIGN_ACTION.EDIT_NOTE,
    status: campaignStatus,
  })

  useEffect(() => {
    if (urlId) {
      VoohCampaignFormService.getVoohCampaign(urlId)
    }
  }, [urlId])

  useEffect(() => {
    setMapMedia(mapVoohMediaNodesToMedia(voohForm.selectedMediums))
  }, [voohForm.selectedMediums])

  useEffect(
    () => () => {
      store.dispatch(voohCampaignFormActions.resetCampaignForm())
    },
    []
  )
  const mapVoohMediaNodesToMedia = (voohMediaNodes: VoohMediaNode[]): Medium[] =>
    voohMediaNodes.map((voohMediaNode: VoohMediaNode) => {
      return {
        address: voohMediaNode.address || '',
        agglomeration: {
          id: voohMediaNode.agglomeration_id?.toString() || '',
          name: voohMediaNode.agglomeration?.name || '',
        },
        asId: voohMediaNode.asId || '',
        building: {
          id: voohMediaNode.building_id?.toString() || '',
          name: voohMediaNode.name,
        },
        city: {
          id: voohMediaNode.city_id?.toString() || '',
          name: voohMediaNode.city?.name || '',
        },
        comment: '',
        id: voohMediaNode.id.toString(),
        images: [],
        lat: parseFloat(voohMediaNode.latitude || ''),
        lon: parseFloat(voohMediaNode.longitude || ''),
        mediaFormat: VoohMediaFormat.MOVE_TV,
        placement: MediumPlacement.INDOOR,
        screenCount: 1,
        status: t(`${voohMediaNode.status}`) || MediumStatus.NOT_OPERATING,
        used: true,
        vac: 1,
        poiCategories: [],
        creationFormat: 'video',
      }
    })

  const saveSelectedVoohMediums = () => {
    const selectedVoohMediaNodes: VoohMediaNode[] = []
    JSON.stringify(voohForm.mediums, (_, nestedValue) => {
      if (
        nestedValue &&
        !nestedValue?.children &&
        checkedNodes.some(el => el.split('_')[0] === nestedValue.id?.toString())
      ) {
        selectedVoohMediaNodes.push({ ...nestedValue })
      }
      return nestedValue
    })

    setMapMedia(mapVoohMediaNodesToMedia(selectedVoohMediaNodes))
    store.dispatch(voohCampaignFormActions.setSelectedVoohMedia(selectedVoohMediaNodes))
    const allMediaNodes: VoohUsedMedia[] = []
    JSON.stringify(voohForm.mediums, (_, nestedValue) => {
      if (nestedValue.used !== undefined) {
        allMediaNodes.push({
          id: nestedValue.id,
          used: selectedVoohMediaNodes.some(el => el.id === nestedValue.id),
        })
      }
      return nestedValue
    })

    VoohCampaignFormService.updateVoohMediaUsage(voohForm.id, allMediaNodes)
  }

  useEffect(() => {
    if (!touched) {
      return
    }
    const errors = VoohCampaignFormService.validate(
      voohForm.baseValues,
      campaignStatus,
      t,
      allowedFor
    )

    if (!VoohCampaignFormService.hasErrors(errors)) {
      debouncedBasicValuesSubmit(voohForm, navigate)
    }
  }, [voohForm.baseValues])

  useEffect(() => {
    if (!touched) {
      return
    }
    debouncedDetailsValuesSubmit(voohForm)
  }, [voohForm.detailsValues])

  useEffect(() => {
    if (!isMediaSelectionModalOpen) {
      setFilterNodesText('')
    }
  }, [isMediaSelectionModalOpen])

  const flattenedMediaTreeNodeValues: string[] = []
  let mediaSize = 0
  const isAnyCreation = voohForm.creations.length > 0

  const loadVoohUploudIcon = (): void => {
    import(`images/dg-cs.svg`).then(image => void setIcon(image.default))
  }

  const buildMediaTree = () => {
    mediaSize = 0
    const mediaTreeCheckboxNodes = voohMediaSelectionService.buildMediaTreeCheckboxNodes(
      mediaTree,
      voohForm.detailsValues.offer,
      userData.user?.roles,
      flattenedMediaTreeNodeValues,
      mediaSize,
      t
    )
    const checkedMediaElements = flattenedMediaTreeNodeValues.filter(el =>
      voohForm.selectedMediums.some(media => media.id.toString() === el.split('_')[0])
    )

    setTimeout(() => {
      setNodes(mediaTreeCheckboxNodes)
      setFilteredNodes([
        {
          label:
            t('form.campaign.details.voohMediaNodes.allClubs') +
            ` (${voohForm.selectedMediums.length}/${flattenedMediaTreeNodeValues.length})`,
          value: 'all',
          children: mediaTreeCheckboxNodes,
        },
      ])
    }, 0)

    setCheckedNodes(checkedMediaElements)
  }

  const MediaSelectionFooter = () => {
    return shouldSaveMedia ? (
      <FillingSpinner type={FillingSpinnerType.STANDARD} />
    ) : (
      <div className='Modal__footer MediaSelectionModal__footer'>
        <Button
          onClick={() => {
            saveSelectedVoohMediums()
            if (saveTemplate) {
              setTemplateMedia(true)
            } else {
              setIsMediaSelectionModalOpen(false)
            }
          }}
        >
          {t('common.save')}
        </Button>
        <Button
          theme={ButtonTheme.BLUE_OUTLINE}
          onClick={() => setIsMediaSelectionModalOpen(false)}
        >
          {t('common.back')}
        </Button>
        <Checkbox
          checked={saveTemplate}
          onChange={e => setSaveTemplate(e.target.checked)}
          className={'MediaSelectionModal__footer-checkbox'}
        >
          {t('mediaSelection.template.save')}
        </Checkbox>
      </div>
    )
  }

  const creationContainer = (creation: VoohCreation): ReactNode => {
    return (
      <div key={creation.id}>
        <FlexRow className='CreationsFormPreview__creation--row'>
          <FlexCol>
            {creation.archivedAt && (
              <TooltipHelp containerClassName='CreationsFormPreview__creation--tooltip'>
                {t('common.archivedInfo')}
              </TooltipHelp>
            )}
            <Preview
              className={
                'CreationsFormPreview__creation--preview' +
                TsxUtils.extraStyle(creation.archivedAt, 'CreationsFormPreview--grayed-out')
              }
              src={creation.file?.url || ''}
              thumbnailSrc={creation.thumbnailUrl}
              isVideo={false}
              alt={''}
            />
          </FlexCol>

          <FlexCol>
            <div
              className={
                'CreationsFormPreview__creation--name' +
                TsxUtils.extraStyle(creation.archivedAt, 'CreationsFormPreview--grayed-out')
              }
            >
              <div className={'CreationsFormPreview__creation__text'}>
                {creation.file?.filename}
                {creation.backendId != null ? ` (id: ${creation.backendId})` : ''}
              </div>
            </div>

            <div className='CreationsFormPreview__creation-details--container'>
              <p className='CreationsFormPreview__creation-details--date'>
                {CreationHelper.getCreationDateText(creation)}
              </p>
            </div>
            <div className='CreationsFormPreview__creation--details'>
              <Input
                id='details'
                title={t('common.creationDetails')}
                value={creation.details || ''}
                onChange={value => {
                  const changedCreation = {
                    ...creation,
                    details: value,
                  }
                  store.dispatch(
                    voohCampaignFormActions.updateForm({
                      ...voohForm,
                      creations: voohForm.creations.map(formCreation => {
                        if (formCreation.id === creation.id) {
                          return changedCreation
                        }
                        return formCreation
                      }),
                    })
                  )
                  debouncedCreationUpdate(changedCreation, VoohMediaFormat.MOVE_TV)
                }}
              />
            </div>
            <div className={'CreationsFormPreview__creation--details'}>
              {creation.file?.isVideo || !voohForm.detailsValues.mediaDuration ? (
                <Input
                  disabled={creation.file?.isVideo}
                  placeholder={t('form.campaign.creations.duration')}
                  title={t('form.campaign.creations.duration')}
                  value={
                    voohForm?.creations
                      ?.find(formCreation => formCreation.id === creation.id)
                      ?.duration.toString() || '5'
                  }
                  onChange={value => {
                    const changedCreation = {
                      ...creation,
                      duration: parseInt(value),
                    }

                    store.dispatch(
                      voohCampaignFormActions.updateForm({
                        ...voohForm,
                        creations: voohForm.creations.map(formCreation => {
                          if (formCreation.id === creation.id) {
                            return changedCreation
                          }
                          return formCreation
                        }),
                      })
                    )
                    debouncedCreationUpdate(changedCreation, VoohMediaFormat.MOVE_TV)
                  }}
                />
              ) : (
                <Select
                  id='duration'
                  disabled={!!voohForm.detailsValues.mediaDuration}
                  onChange={value => {
                    const changedCreation = {
                      ...creation,
                      duration: parseInt(value),
                    }

                    store.dispatch(
                      voohCampaignFormActions.updateForm({
                        ...voohForm,
                        creations: voohForm.creations.map(formCreation => {
                          if (formCreation.id === creation.id) {
                            return changedCreation
                          }
                          return formCreation
                        }),
                      })
                    )
                    debouncedCreationUpdate(changedCreation, VoohMediaFormat.MOVE_TV)
                  }}
                  options={MediaDurationHelper.getVoohDurationOptions(
                    t,
                    voohForm.detailsValues.mediaDuration,
                    voohForm.baseValues.agency?.rawData?.agencyType === AgencyType.external
                  )}
                  placeholder={t('form.campaign.creations.duration')}
                  title={t('form.campaign.creations.duration')}
                  value={
                    voohForm?.creations
                      ?.find(formCreation => formCreation.id === creation.id)
                      ?.duration.toString() || '5'
                  }
                />
              )}
            </div>
            <div className='CreationsFormPreview__creation--details'>
              <Input
                id='frequency'
                type={InputType.NUMBER}
                title={t('form.campaign.creations.frequency')}
                value={
                  voohForm?.creations?.find(formCreation => formCreation.id === creation.id)
                    ?.hourFrequency || ''
                }
                onChange={value => {
                  const changedCreation = {
                    ...creation,
                    hourFrequency: parseInt(value),
                  }
                  store.dispatch(
                    voohCampaignFormActions.updateForm({
                      ...voohForm,
                      creations: voohForm.creations.map(formCreation => {
                        if (formCreation.id === creation.id) {
                          return changedCreation
                        }
                        return formCreation
                      }),
                    })
                  )
                  debouncedCreationUpdate(changedCreation, VoohMediaFormat.MOVE_TV)
                }}
              />
            </div>

            <div className='CreationsFormPreview__creation-details--container'>
              <Link
                className='CreationsFormPreview__creation-details'
                toUrl={Routes.CAMPAIGNS.EMISSION_SCHEDULE({
                  campaignId: voohForm.id,
                  creationId: creation.id,
                  readOnly: '',
                  vooh: true,
                })}
              >
                {t('form.campaign.creations.editEmission')}
              </Link>
              {creation.archivedAt && (
                <Link
                  className='CreationsFormPreview__creation-details--archived'
                  onClick={() => void setEditArchivedModalOpenFor(creation.id)}
                >
                  {t('common.archived')}
                </Link>
              )}
            </div>
          </FlexCol>
          <FlexCol>
            {allowedFor({
              template: CAMPAIGN_FORM.REMOVE_CREATION_BY_CAMPAIGN_STATUS,
              status: campaignStatus,
            }) &&
              campaignStatus === CampaignStatus.SKETCH && (
                <Button
                  icon={faTrashAlt}
                  size={ButtonSize.SMALL}
                  theme={ButtonTheme.NAVY_BLUE}
                  tooltip={t('common.remove')}
                  onClick={() => {
                    setRemoveCreationModalIsOpenFor(creation.id)
                  }}
                />
              )}
            {allowedFor({
              template: CAMPAIGN_FORM.ARCHIVE_CREATION_BY_CAMPAIGN_STATUS,
              status: campaignStatus,
            }) &&
              !creation.archivedAt &&
              campaignStatus !== CampaignStatus.SKETCH && (
                <>
                  <Button
                    dataCy={'archive-creation-btn'}
                    icon={faArchive}
                    size={ButtonSize.SMALL}
                    theme={ButtonTheme.NAVY_BLUE}
                    tooltip={t('common.archive')}
                    onClick={() => void setArchiveModalIsOpenFor(creation.id)}
                  />
                  <TooltipHelp containerClassName='CreationsFormPreview__button--tooltip'>
                    {t('form.campaign.creations.archiveButtonInfo')}
                  </TooltipHelp>
                </>
              )}
          </FlexCol>
          {removeCreationModalIsOpenFor?.includes(creation.id) && (
            <ModalAction
              title={t('modal.creationRemoval')}
              itemText={t('modal.doYouWantToRemove')}
              itemName={creation.file?.filename}
              actionText={t('common.remove')}
              isOpen={!!removeCreationModalIsOpenFor}
              setIsOpen={() => void setRemoveCreationModalIsOpenFor(null)}
              onAction={() => {
                setDeleteCreationLoading(true)
                return VoohCampaignFormService.deleteVoohCreation(creation.id).then(() => {
                  setDeleteCreationLoading(false)
                })
              }}
              isLoading={deleteCreationLoading}
              setIsLoading={setDeleteCreationLoading}
            />
          )}
          {archiveModalIsOpenFor?.includes(creation.id) && (
            <ModalAction
              actionText={t('modal.archive')}
              isLoading={archiveCreationLoading}
              isOpen={!!archiveModalIsOpenFor}
              itemName={creation.file?.filename}
              itemText={t('modal.doYouWantToArchive')}
              onAction={(reason?: string) => {
                setArchiveCreationLoading(true)
                return VoohCampaignFormService.archiveVoohCreation(creation.id, reason || '').then(
                  () => {
                    setArchiveCreationLoading(false)
                  }
                )
              }}
              setIsLoading={setArchiveCreationLoading}
              setIsOpen={() => void setArchiveModalIsOpenFor(null)}
              textInputHeader={t('common.reason')}
              textInputInitValue={creation.archiveReason || ''}
              title={t('modal.creationArchiving')}
            />
          )}

          {editArchivedModalOpenFor?.includes(creation.id) && (
            <ModalAction
              actionText={t('modal.archive')}
              isLoading={archiveCreationLoading}
              isOpen={!!editArchivedModalOpenFor}
              onAction={(reason?: string) => {
                setArchiveCreationLoading(true)
                return VoohCampaignFormService.archiveVoohCreation(creation.id, reason || '').then(
                  () => {
                    setArchiveCreationLoading(false)
                  }
                )
              }}
              setIsLoading={setArchiveCreationLoading}
              setIsOpen={() => void setEditArchivedModalOpenFor(null)}
              textInputHeader={t('common.reason')}
              textInputInitValue={creation.archiveReason}
              title={t('modal.archivedCreation') + ` ${creation.file?.filename}`}
            />
          )}
        </FlexRow>
      </div>
    )
  }

  useEffect(() => {
    if (mediaTree) {
      buildMediaTree()
    }
  }, [mediaTree, voohForm.detailsValues.offer])

  useEffect(() => {
    if (!filterNodesText) {
      setFilteredNodes([
        {
          label: t('form.campaign.details.voohMediaNodes.allClubs'),
          value: 'all',
          children: nodes,
        },
      ])
      return
    } else {
      setFilteredNodes([
        {
          label: t('form.campaign.details.voohMediaNodes.allClubs'),
          value: 'all',
          children: nodes.reduce(voohMediaSelectionService.filterNodes(filterNodesText), []),
        },
      ])
    }
  }, [filterNodesText])

  const handleApplyFilterByIds = useCallback(
    (ids: string, mode: 'replace' | 'append') => {
      checkedMediaOnFilterById = []
      if (!ids.trim()) return
      nodes.reduce(voohMediaSelectionService.filterNodesByIds(ids, checkedMediaOnFilterById), [])
      store.dispatch(voohCampaignFormActions.setTouched())
      if (mode === 'append') {
        setCheckedNodes(prev => [...new Set([...prev, ...checkedMediaOnFilterById])])
      } else {
        setCheckedNodes(checkedMediaOnFilterById)
      }
    },
    [nodes]
  )

  useEffect(() => {
    loadVoohUploudIcon()
  }, [])
  return (
    <>
      <div className='CampaignForm__live-media-map'>
        <LiveMediaMap media={mapMedia} />
      </div>

      <div className='CampaignForm'>
        <PageHeader
          className='CampaignForm__header'
          withBorder
        >
          {urlId ? t('common.editVoohCampaign') : t('common.newVoohCampaign')}
        </PageHeader>

        <div className='CampaignForm__summary-card--container'>
          {canEditNote && (
            <Card className='CampaignForm__summary-card'>
              <NoteSummary
                vooh={true}
                campaignId={voohForm.id}
                note={note}
                contractNote={''}
                onEdit={note =>
                  store.dispatch(voohCampaignFormActions.updateVoohCampaignNote(note))
                }
              />
            </Card>
          )}
        </div>

        <Card
          headerNumber={1}
          className='CampaignForm__card CampaignForm__card--basics'
          title={t('form.campaign.basics.header')}
          status={voohCardStatus.basics}
        >
          <div className='DetailsForm--vooh'>
            <FormRow className='DetailsForm--vooh__row'>
              <FormColumn>
                <Input
                  id='name'
                  title={t('form.campaign.basics.name')}
                  placeholder={t('form.campaign.basics.namePlaceholder')}
                  value={voohForm.baseValues.name}
                  errors={voohFormErrors?.name}
                  onChange={val => {
                    store.dispatch(
                      voohCampaignFormActions.updateForm({
                        ...voohForm,
                        baseValues: {
                          ...voohForm.baseValues,
                          name: val,
                        },
                      })
                    )
                  }}
                />
              </FormColumn>
            </FormRow>

            <FormRow className='DetailsForm--vooh__row'>
              <FormColumn>
                <Input
                  id='briefName'
                  title={t('common.briefName')}
                  placeholder={t('form.campaign.basics.briefNamePlaceholder')}
                  value={voohForm.baseValues.briefName}
                  errors={voohFormErrors?.briefName}
                  onChange={val => {
                    store.dispatch(
                      voohCampaignFormActions.updateForm({
                        ...voohForm,
                        baseValues: {
                          ...voohForm.baseValues,
                          briefName: val,
                        },
                      })
                    )
                  }}
                />
              </FormColumn>
            </FormRow>
            {allowedFor({ template: CAMPAIGN_FORM.AGENCY, status: campaignStatus }) && (
              <FormRow className='BasicsForm__row'>
                <SelectAsync
                  id='agency'
                  title={t('common.agency')}
                  help={t('form.campaign.basics.agencyHelp')}
                  placeholder={t('form.campaign.basics.agencyNamePlaceholder')}
                  value={voohForm.baseValues.agency}
                  errors={voohFormErrors?.agency}
                  options={VoohCampaignFormService.getAgencies}
                  onChange={(agency): void => {
                    store.dispatch(
                      voohCampaignFormActions.updateForm({
                        ...voohForm,
                        baseValues: {
                          ...voohForm.baseValues,
                          agency: agency,
                        },
                      })
                    )
                  }}
                  // todo for now we assume having only one agency
                  disabled={true}
                />
              </FormRow>
            )}

            <FormRow className='BasicsForm__row'>
              <SelectAsync
                id='advertiser'
                title={t('common.advertiser')}
                help={t('form.campaign.basics.advertiserHelp')}
                placeholder={t('common.advertiser')}
                value={voohForm.baseValues.advertiser}
                isClearable={true}
                label={t('common.search') + '...'}
                options={VoohCampaignFormService.getCompanies}
                onChange={(advertiser): void => {
                  store.dispatch(
                    voohCampaignFormActions.updateForm({
                      ...voohForm,
                      baseValues: {
                        ...voohForm.baseValues,
                        advertiser: advertiser,
                      },
                    })
                  )
                }}
                asyncFiltering={true}
              />
            </FormRow>

            {allowedFor({ template: CAMPAIGN_FORM.SUPERVISORS, status: campaignStatus }) && (
              <FormRow className='BasicsForm__row'>
                <SelectAsync
                  id='supervisors'
                  title={t('common.supervisors')}
                  placeholder={t('form.campaign.basics.supervisorsPlaceholder')}
                  value={voohForm.baseValues.supervisors}
                  onChange={(supervisors): void => {
                    store.dispatch(
                      voohCampaignFormActions.updateForm({
                        ...voohForm,
                        baseValues: {
                          ...voohForm.baseValues,
                          supervisors: supervisors,
                        },
                      })
                    )
                  }}
                  options={VoohCampaignFormService.getUsersForSupervisors}
                  multi
                  disabled={disabledSupervisors()}
                  closeOnSelect={false}
                />
              </FormRow>
            )}

            <FormRow className='BasicsForm__row'>
              <SelectAsync
                id='brandNetwork'
                title={t('common.brandNetwork')}
                placeholder={t('common.brandNetwork')}
                value={voohForm.baseValues.clubMediaSource}
                isClearable={true}
                options={VoohCampaignFormService.getVoohBrandsNetworks}
                onChange={(clubMediaSource): void => {
                  if (voohForm.baseValues.clubMediaSource === clubMediaSource) {
                    return
                  }

                  store.dispatch(
                    voohCampaignFormActions.updateForm({
                      ...voohForm,
                      baseValues: {
                        ...voohForm.baseValues,
                        clubMediaSource: clubMediaSource,
                      },
                    })
                  )
                }}
              />
            </FormRow>

            <FormRow className='BasicsForm__row'>
              <FormColumn className='BasicsForm__date-input--column'>
                <DatePickerSingle
                  id='startDate'
                  value={voohForm.baseValues.startDate}
                  title={t('form.campaign.basics.startDate')}
                  placeholder={DATE_PLACEHOLDER}
                  errors={voohFormErrors?.startDate}
                  isOutsideRange={day => {
                    return VoohCampaignFormService.isNotAllowedDate(
                      day,
                      voohForm.baseValues.endDate,
                      voohForm.baseValues.startDate,
                      true
                    )
                  }}
                  disabled={campaignStatus === CampaignStatus.ACTIVE}
                  onChange={val => {
                    store.dispatch(
                      voohCampaignFormActions.updateForm({
                        ...voohForm,
                        baseValues: {
                          ...voohForm.baseValues,
                          startDate: val,
                        },
                      })
                    )
                  }}
                />
              </FormColumn>

              <FormColumn className='BasicsForm__date-input--column'>
                <DatePickerSingle
                  id='endDate'
                  value={voohForm.baseValues.endDate}
                  errors={voohFormErrors?.endDate}
                  title={t('form.campaign.basics.endDate')}
                  placeholder={DATE_PLACEHOLDER}
                  isOutsideRange={day =>
                    VoohCampaignFormService.isNotAllowedDate(
                      day,
                      voohForm.baseValues.endDate,
                      voohForm.baseValues.startDate,
                      false
                    )
                  }
                  onChange={val => {
                    store.dispatch(
                      voohCampaignFormActions.updateForm({
                        ...voohForm,
                        baseValues: {
                          ...voohForm.baseValues,
                          endDate: val,
                        },
                      })
                    )
                  }}
                />
              </FormColumn>
            </FormRow>
          </div>
        </Card>

        <Card
          headerNumber={2}
          className='CampaignForm__card'
          title={t('common.campaignDetails')}
          status={voohCardStatus.details}
          disabled={!voohForm.id}
        >
          <div className='DetailsForm--vooh'>
            {/*todo: uncomment when more formats needed*/}
            {/*<FormRow className='DetailsForm--vooh__row'>*/}
            {/*  <DetailsFormMediaFormat*/}
            {/*    voohCampaign={true}*/}
            {/*    value={voohForm.detailsValues.mediaFormat}*/}
            {/*    onChange={() => {*/}
            {/*      //todo: handle more formats in the future*/}
            {/*    }}*/}
            {/*  />*/}
            {/*</FormRow>*/}

            <FormRow className='DetailsForm--vooh__row'>
              <FormColumn>
                <Input
                  title={t('form.campaign.details.hourFrequency')}
                  value={
                    voohForm.detailsValues.hourFrequency
                      ? voohForm.detailsValues.hourFrequency.toString()
                      : ''
                  }
                  onChange={(val: string) => {
                    store.dispatch(
                      voohCampaignFormActions.updateForm({
                        ...voohForm,
                        detailsValues: {
                          ...voohForm.detailsValues,
                          hourFrequency: val ? parseInt(val, 10) : null,
                        },
                      })
                    )
                  }}
                />
              </FormColumn>
            </FormRow>
            <FormRow className='DetailsForm--vooh__row'>
              <Select
                clearable
                id='offer'
                onChange={(val: VOOH_OFFER) => {
                  store.dispatch(
                    voohCampaignFormActions.updateForm({
                      ...voohForm,
                      detailsValues: {
                        ...voohForm.detailsValues,
                        offer: val,
                      },
                    })
                  )
                }}
                options={offerService.getVoohOffersOptions()}
                placeholder={t('form.campaign.details.offerPlaceholder')}
                title={t('common.offerType')}
                value={voohForm.detailsValues.offer}
              />
            </FormRow>
            {voohForm.baseValues.agency?.rawData?.agencyType === AgencyType.internal && (
              <FormRow className='DetailsForm--vooh__row'>
                <Select
                  id='mediaDuration'
                  onChange={(val: string) => {
                    store.dispatch(
                      voohCampaignFormActions.updateCreationsDuration(val ? parseInt(val) : null)
                    )
                  }}
                  options={MediaDurationHelper.getVoohDurationOptions(t)}
                  placeholder={t('common.mediaDuration')}
                  title={t('common.mediaDuration')}
                  value={
                    voohForm.detailsValues.mediaDuration
                      ? voohForm.detailsValues.mediaDuration.toString()
                      : ''
                  }
                />
              </FormRow>
            )}

            <FormRow className='DetailsForm--vooh__row DetailsForm--vooh__row--locations'>
              <InputTitle title={t('common.locations')} />

              <div className='DetailsForm--vooh__media'>
                <Button
                  onClick={() => {
                    buildMediaTree()
                    setIsMediaSelectionModalOpen(true)
                    setSaveTemplate(false)
                  }}
                >
                  {t('common.select')}
                </Button>
                <Link
                  className={'DetailsForm--vooh__media--list'}
                  onClick={() => {
                    buildMediaTree()
                    setIsMediaTableOpen(true)
                  }}
                >
                  {t('common.usedClubs')}
                </Link>
              </div>
            </FormRow>

            {isMediaSelectionModalOpen &&
              (templateMedia ? (
                <VoohCreateMediaTemplateModal
                  media={voohForm.selectedMediums.map(el => el.id) as number[]}
                  close={() => {
                    //todo: handle templates
                    setTemplateMedia(false)
                    setIsMediaSelectionModalOpen(false)
                  }}
                  goBack={() => {
                    //todo: handle templates
                    setTemplateMedia(false)
                    setIsMediaSelectionModalOpen(false)
                  }}
                />
              ) : (
                <Modal
                  classNameChildren={TsxUtils.joinClasses(
                    'MediaSelectionModal',
                    'MediaSelectionModal--dg-in'
                  )}
                  title={t('mediaSelection.clubsSelectionTitle')}
                  isOpen={isMediaSelectionModalOpen}
                  onClose={setIsMediaSelectionModalOpen}
                  footer={MediaSelectionFooter()}
                  scrollable
                >
                  <VoohMediaSelection
                    onCheckMedia={() => {
                      store.dispatch(voohCampaignFormActions.setTouched())
                    }}
                    filterNodesText={filterNodesText}
                    setFilterNodesText={setFilterNodesText}
                    setCheckedNodes={setCheckedNodes}
                    filteredNodes={filteredNodes}
                    checked={checkedNodes}
                    voohMediaTemplateFormat={voohForm.detailsValues.mediaFormat}
                    onApplyFilterByIds={handleApplyFilterByIds}
                  />
                </Modal>
              ))}

            {isMediaTableOpen && (
              <Modal
                classNameChildren={TsxUtils.joinClasses(
                  'MediaSelectionModal',
                  'MediaSelectionModal--dg-in'
                )}
                title={t('form.campaign.details.voohMediaNodes.chosenCLubs')}
                isOpen={isMediaTableOpen}
                onClose={() => setIsMediaTableOpen(false)}
                footer={
                  <Button onClick={() => void setIsMediaTableOpen(false)}>
                    {t('common.back')}
                  </Button>
                }
              >
                <VoohMediaListTable
                  media={voohMediaSelectionService.getSelectedMediaArrayFromTree(
                    mediaTree,
                    checkedNodes
                  )}
                ></VoohMediaListTable>
              </Modal>
            )}

            <FormRow className='DetailsForm--vooh__row'>
              <FormColumn>
                <InputTitle title={t('attributesSelection.organizationTitle')} />
                <Button onClick={() => setIsAttributesModalOpen(true)}>{t('common.select')}</Button>
              </FormColumn>
            </FormRow>
            {isAttributesModalOpen && (
              <AttributesModal
                showClubsSelfPromotion={true}
                isOpen={isAttributesModalOpen}
                setIsOpen={setIsAttributesModalOpen}
                organizationAttribute={voohForm.detailsValues.organizationAttribute}
                updateOrganizationAttributes={(organizationAttributes: OrganizationAttributes) => {
                  store.dispatch(
                    voohCampaignFormActions.updateForm({
                      ...voohForm,
                      detailsValues: {
                        ...voohForm.detailsValues,
                        organizationAttribute: organizationAttributes,
                      },
                    })
                  )
                }}
              />
            )}
          </div>
        </Card>

        <Card
          disabled={!voohForm.id}
          id={CampaignFormHashes.CREATIONS}
          headerNumber={3}
          className='CampaignForm__card'
          title={t('form.campaign.creations.header')}
          status={voohCardStatus.creations}
          noMarginBody
          isCollapsed={isCreationsCardCollapsed}
          onCollapse={isCollapsed => void setIsCreationsCardCollapsed(isCollapsed)}
        >
          {!isCreationsCardCollapsed && (
            <div className='CreationsForm'>
              <div className='CreationsFormPreview'>
                <div className='CreationsFormPreview__bar'>
                  <img
                    className='CreationsFormPreview__bar--icon'
                    src={icon}
                  />
                  <div className='CreationsFormPreview__bar--title'>{t(`mediaType.moveTv`)}</div>
                  <Button
                    className='CreationsFormPreview__bar--add'
                    dataCy={'add-creation-btn'}
                    icon={faPlus}
                    size={ButtonSize.SMALL}
                    tooltip={t('form.campaign.creations.addCreation')}
                    onClick={() => {
                      void setIsAddCreationModalOpen(true)
                    }}
                  />
                </div>

                <div
                  className={
                    'CreationsFormPreview__creation' +
                    TsxUtils.extraStyle(!isAnyCreation, 'CreationsFormPreview__creation--empty')
                  }
                >
                  {voohForm.creations.length
                    ? voohForm.creations.map((c: VoohCreation) => creationContainer(c))
                    : t('form.campaign.creations.noCreation')}
                </div>

                {isAddCreationModalOpen && (
                  <Modal
                    title={t('form.campaign.creations.addingCreations')}
                    isOpen={isAddCreationModalOpen}
                    onClose={(isOpen: boolean) => void setIsAddCreationModalOpen(isOpen)}
                    classNameContent={'Modal__content--creations-form-upload'}
                    footer={
                      <Button
                        theme={ButtonTheme.BLUE_OUTLINE}
                        onClick={() => void setIsAddCreationModalOpen(false)}
                      >
                        {t('common.cancel')}
                      </Button>
                    }
                  >
                    <VoohCreationsFormUpload
                      maxVideoLength={voohForm.detailsValues.mediaDuration}
                      onFinish={() => void setIsAddCreationModalOpen(false)}
                    />
                  </Modal>
                )}
              </div>
            </div>
          )}
        </Card>
        {voohForm.id && <VoohCampaignActionButtons formType={formType} />}
      </div>
    </>
  )
}

export default VoohCampaignForm
